import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";
import { PageSection } from "@/components/site/PageSection";
import {
  Callout,
  FaqList,
  H2,
  InsuranceCTA,
  P,
  SITE,
  UL,
  faqSchema,
  medicalWebPageSchema,
  type Faq,
} from "@/components/site/InsuranceProse";
import { TelehealthCta } from "@/components/site/TelehealthCta";

const TITLE = "Do I Qualify for Weight-Loss Medication? | Virginia Bariatric";
const DESCRIPTION =
  "BMI criteria for Zepbound, Wegovy, and Saxenda, off-label options for self-pay patients, and what Virginia insurers actually require.";
const URL = `${SITE}/medical-weight-loss/qualifications`;
const NAME = "Do You Qualify for Injectable Weight-Loss Medication?";

const LABELS: { drug: string; indications: string }[] = [
  {
    drug: "Zepbound (tirzepatide)",
    indications:
      "Weight reduction and long-term maintenance in adults with obesity, or adults with overweight plus at least one weight-related condition. Also: moderate-to-severe obstructive sleep apnea in adults with obesity.",
  },
  {
    drug: "Wegovy injection (semaglutide)",
    indications:
      "Weight reduction in adults and patients 12 and older with obesity, or adults with overweight plus a weight-related condition. Also: reducing cardiovascular events in adults with established heart disease; and MASH with stage F2\u2013F3 liver fibrosis.",
  },
  {
    drug: "Wegovy tablet and Wegovy HD",
    indications: "Same weight and cardiovascular indications, adults only.",
  },
  {
    drug: "Saxenda (liraglutide, incl. generic)",
    indications:
      "Weight reduction in adults, and patients 12 and older weighing more than 60 kg, with obesity \u2014 or adults with overweight plus a weight-related condition.",
  },
];

const PLANS: { plan: string; bmi: string; other: string }[] = [
  {
    plan: "Virginia Medicaid (Cardinal Care)",
    bmi: "Over 40 \u2014 or over 37 with high cholesterol, high blood pressure, or type 2 diabetes",
    other:
      "Trial and failure of a non-GLP-1 weight-loss drug; nutritional counseling and activity program; attestation that obesity is disabling and life-threatening; height and weight within 60 days",
  },
  {
    plan: "Virginia state employee plans (COVA)",
    bmi: "35 or higher \u2014 raised from 27/30 on July 1, 2026",
    other:
      "Current patients become subject to the new threshold when their existing authorization expires",
  },
  {
    plan: "Sentara Health Plans",
    bmi: "30 \u2014 or 27 with a qualifying condition",
    other:
      "Weight-loss treatment plan within the past 6 months, continuing during therapy; current height, weight, and BMI; not using another GLP-1",
  },
  {
    plan: "UnitedHealthcare",
    bmi: "30 \u2014 or 27 with a weight-related condition",
    other:
      "Used alongside lifestyle modification. Applies only if your employer elected weight-loss coverage. No step therapy",
  },
  {
    plan: "Cigna",
    bmi: "30 \u2014 or 27 with a qualifying condition",
    other: "At least 3 months of behavioral and dietary change; age 18 or older",
  },
  {
    plan: "Anthem Blue Cross and Blue Shield",
    bmi: "Generally 30 \u2014 or 27 with a comorbidity",
    other:
      "Documented lifestyle attempts. Varies by employer group; many Anthem plans exclude weight-loss drugs entirely",
  },
  {
    plan: "Aetna",
    bmi: "Generally 30 \u2014 or 27 with a comorbidity; some employers select a BMI 35 policy",
    other: "Documented participation in a weight management program",
  },
  {
    plan: "TRICARE",
    bmi: "30 \u2014 or 27 to 29 with a weight-related condition",
    other:
      "6 months of documented behavioral and dietary change; a 3-month trial of a generic weight-loss drug that failed to produce 5% loss; prescriber must be an MTF or TRICARE network provider",
  },
  {
    plan: "Medicare GLP-1 Bridge",
    bmi: "Recorded at start of therapy; no fixed cutoff published",
    other:
      "For adults with no Part D-eligible diagnosis. Runs through December 31, 2027 at a $50 monthly copay",
  },
  {
    plan: "Virginia marketplace and small-group plans",
    bmi: "Not applicable",
    other:
      "Virginia's benchmark plan excludes weight-loss drugs, so these plans generally do not cover them at any BMI",
  },
];

type Source = { label: string; href: string };

// FDA-approved prescribing information (Drugs@FDA / DailyMed)
const LABEL_SOURCES: Source[] = [
  {
    label: "Zepbound label (FDA)",
    href: "https://www.accessdata.fda.gov/scripts/cder/daf/index.cfm?event=overview.process&ApplNo=217806",
  },
  {
    label: "Wegovy label (FDA)",
    href: "https://www.accessdata.fda.gov/scripts/cder/daf/index.cfm?event=overview.process&ApplNo=215256",
  },
  {
    label: "Saxenda label (FDA)",
    href: "https://www.accessdata.fda.gov/scripts/cder/daf/index.cfm?event=overview.process&ApplNo=206321",
  },
  {
    label: "DailyMed labeling search",
    href: "https://dailymed.nlm.nih.gov/dailymed/",
  },
];

// Payor policy documents behind the BMI criteria in the table above
const PLAN_SOURCES: Source[] = [
  {
    label: "Virginia Medicaid (DMAS) pharmacy service authorization",
    href: "https://www.virginiamedicaidpharmacyservices.com/",
  },
  {
    label: "Virginia state employee plans (DHRM benefits)",
    href: "https://www.dhrm.virginia.gov/employeebenefits/health-benefits",
  },
  {
    label: "Sentara Health Plans pharmacy services",
    href: "https://www.sentarahealthplans.com/providers",
  },
  {
    label: "UnitedHealthcare pharmacy prior authorization",
    href: "https://www.uhcprovider.com/en/prior-auth-advance-notification/pharmacy-prior-auth.html",
  },
  {
    label: "Cigna coverage policies (pharmacy)",
    href: "https://www.cigna.com/health-care-providers/coverage-and-claims/policies",
  },
  {
    label: "Anthem clinical guidelines and pharmacy policies",
    href: "https://www.anthem.com/provider/policies/clinical-guidelines/",
  },
  {
    label: "Aetna pharmacy clinical policy bulletins",
    href: "https://www.aetna.com/health-care-professionals/clinical-policy-bulletins/pharmacy-clinical-policy-bulletins.html",
  },
  {
    label: "TRICARE formulary and prior authorization forms",
    href: "https://militaryrx.express-scripts.com/formulary-search",
  },
  {
    label: "CMS Medicare prescription drug coverage guidance",
    href: "https://www.cms.gov/medicare/coverage/determination-process",
  },
  {
    label: "Virginia essential health benefits benchmark plan (SCC Bureau of Insurance)",
    href: "https://scc.virginia.gov/pages/Health-Insurance",
  },
];

const FAQS: Faq[] = [
  {
    q: "What does on-label mean for weight-loss medication?",
    a: "On-label indications are the uses the FDA approved, drawn from the drug's pivotal clinical trial. For semaglutide and tirzepatide, those trials included minimum BMI thresholds, so the BMI requirements became part of the approved labeling.",
  },
  {
    q: "Can I get weight-loss medication off-label?",
    a: "Yes, if you are paying out-of-pocket. Off-label prescribing by a licensed clinician is lawful and routine. Virginia Bariatric staff have discretion to depart from on-label criteria, including BMI limits, on a case-by-case basis.",
  },
  {
    q: "What BMI do Virginia insurers require?",
    a: "Most commercial plans require a BMI of 30, or 27 with a weight-related condition. Virginia Medicaid generally requires over 40, or over 37 with a qualifying condition, and state employee (COVA) plans raised their threshold to 35 on July 1, 2026.",
  },
  {
    q: "Why was my prior authorization denied?",
    a: "The most common reason for denial is not BMI. It is missing documentation \u2014 a lifestyle attempt that happened but was never charted, or a qualifying condition that exists in your history but was not submitted.",
  },
  {
    q: "What happens at renewal?",
    a: "Most plans approve an initial course of 4 to 12 months and require documented weight loss of at least 5% \u2014 4% for Saxenda \u2014 before renewing. Virginia Medicaid stops renewals once BMI falls below 25.",
  },
];

export const Route = createFileRoute("/medical-weight-loss/qualifications")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: URL },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { property: "og:image:type", content: "image/webp" },
        { property: "og:image:width", content: "1280" },
        { property: "og:image:height", content: "720" },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
        { property: "article:modified_time", content: "2026-08-09" },
      ] : []),
    ],
    links: [
      ...(import.meta.env.SSR ? canonicalAndAlternates("/medical-weight-loss/qualifications") : []),
    ],
    scripts: import.meta.env.SSR ? [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS, URL)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          medicalWebPageSchema({
            url: URL,
            name: NAME,
            description: DESCRIPTION,
            image: {
              url: SOCIAL_PREVIEW_IMAGE,
              alt: SOCIAL_PREVIEW_ALT,
              width: 1280,
              height: 720,
            },
            hasFaq: true,
            audienceType: "Patients seeking weight-loss medication coverage",
          })
        ),
      },
    ] : [],
  }),
  component: MedicationQualifications,
});

function MedicationQualifications() {
  return (
    <>
      <PageSection className="pt-8 pb-4 text-center">
        <h1 className="text-3xl md:text-4xl font-serif">
          Do You Qualify for Injectable Weight-Loss Medication?
        </h1>
        <p className="mt-4 max-w-3xl mx-auto text-foreground/85 leading-[1.6]">
          On-label criteria, off-label prescribing, and what your insurer requires.
        </p>
        <TelehealthCta />

      </PageSection>

      <PageSection maxWidth="4xl" className="pb-4">
        <H2 id="on-label">On-label and off-label prescriptions</H2>
        <P>
          Prescriptions are classified as either &ldquo;on label&rdquo; or &ldquo;off label&rdquo;
          depending on the specific medical condition &mdash; the indication, in doctor-speak
          &mdash; for which it was written. On-label indications are the ones approved by the FDA.
          When the FDA notifies a drug manufacturer that its product got FDA approval, the approval
          letter lays out the specific indications the approval covers. They are pulled directly
          from a drug&rsquo;s pivotal study, a special clinical trial that forms the basis for FDA
          approval.
        </P>
        <P>
          The pivotal studies of semaglutide and tirzepatide included, among other patient-selection
          criteria, minimum BMI thresholds. Those BMI requirements were incorporated into the
          on-label indications.
          <a href="#fn1" id="fnref1" className="text-brand-deep align-super text-xs no-underline">
            1
          </a>
        </P>
        <P>
          For most drugs, it&rsquo;s eventually determined that they are useful for conditions
          beyond the on-label indications. These add-on indications are the &ldquo;off-label&rdquo;
          ones. Unfortunately, health insurers tend to only cover the on-label indications.
        </P>

        <Callout>
          <strong>
            If you are relying on health insurance to cover your medication, then you must meet the
            on-label BMI thresholds &mdash; possibly even stricter ones.
          </strong>
        </Callout>

        <Callout>
          <strong>
            If you are paying out-of-pocket, you aren&rsquo;t bound by on-label criteria &mdash;
            including BMI limits.
          </strong>{" "}
          Virginia Bariatric staff have discretion to depart from the on-label criteria. The medical
          literature is full of reports of weight-loss medications being safely used for myriad
          off-label issues. Semaglutide and tirzepatide are generally quite safe and have a
          comparatively benign adverse-event profile, and they can be prescribed safely in many
          circumstances. Decisions whether to go off-label are made on a case-by-case basis.
        </Callout>

        <H2 id="labels">What &ldquo;on-label&rdquo; says for these medications</H2>
        <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="FDA-approved indications for injectable weight-loss medications">
          <table className="w-full text-sm border-collapse">
            <caption className="sr-only">
              FDA-approved indications for injectable weight-loss medications
            </caption>
            <thead>
              <tr className="bg-muted">
                <th scope="col" className="border border-border p-2 text-left align-top w-1/3">
                  Medication
                </th>
                <th scope="col" className="border border-border p-2 text-left align-top">
                  FDA-approved indications
                </th>
              </tr>
            </thead>
            <tbody>
              {LABELS.map((row) => (
                <tr key={row.drug}>
                  <th
                    scope="row"
                    className="border border-border p-2 text-left align-top font-semibold"
                  >
                    {row.drug}
                  </th>
                  <td className="border border-border p-2 align-top">{row.indications}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <P>
          <strong>Important note:</strong> &ldquo;Obesity&rdquo; used here is defined as a BMI of 30
          or above. &ldquo;Overweight&rdquo; is defined by a BMI greater than 25 but less than 30.
          For patients aged 12 to 17, obesity is defined as a BMI at or above the 95th percentile
          for age and sex.
        </P>
        <p className="mt-2 text-sm text-muted-foreground leading-snug">
          Indications above are taken from each product&rsquo;s FDA-approved prescribing
          information. Review the originals:{" "}
          {LABEL_SOURCES.map((s, i) => (
            <span key={s.href}>
              {i > 0 ? " \u00b7 " : ""}
              <a
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-deep underline hover:no-underline"
              >
                {s.label}
              </a>
            </span>
          ))}
          .
        </p>



        <H2 id="insurers">Actual BMI criteria and other medical requirements by insurer</H2>
        <P>
          Current as of July 2026. Employer-sponsored plans can differ from their carrier&rsquo;s
          standard policy, so confirm your own plan&rsquo;s terms.
        </P>
        <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="BMI and documentation requirements by Virginia insurance plan">
          <table className="w-full text-sm border-collapse">
            <caption className="sr-only">
              BMI and documentation requirements by Virginia insurance plan
            </caption>
            <thead>
              <tr className="bg-muted">
                <th scope="col" className="border border-border p-2 text-left align-top">
                  Plan
                </th>
                <th scope="col" className="border border-border p-2 text-left align-top">
                  BMI required
                </th>
                <th scope="col" className="border border-border p-2 text-left align-top">
                  Other requirements
                </th>
              </tr>
            </thead>
            <tbody>
              {PLANS.map((row) => (
                <tr key={row.plan}>
                  <th
                    scope="row"
                    className="border border-border p-2 text-left align-top font-semibold"
                  >
                    {row.plan}
                  </th>
                  <td className="border border-border p-2 align-top">{row.bmi}</td>
                  <td className="border border-border p-2 align-top">{row.other}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-sm text-muted-foreground leading-snug">
          Criteria above are drawn from each payor&rsquo;s published coverage policy and prior
          authorization criteria &mdash; see{" "}
          <a href="#sources" className="text-brand-deep underline hover:no-underline">
            sources and payor policy links
          </a>
          .
        </p>

        <P>
          <strong>Renewals depend on results.</strong> Most plans approve an initial course of 4 to
          12 months and require documented weight loss of at least 5% &mdash; 4% for Saxenda &mdash;
          before renewing. Virginia Medicaid stops renewals once BMI falls below 25.
        </P>
        <P>
          The <strong>most common reason for denial is not BMI</strong>. It is missing documentation
          &mdash; a lifestyle attempt that happened but was never charted, or a qualifying condition
          that exists in your history but wasn&rsquo;t submitted. The more complete your record is
          before we file, the better your odds.
        </P>

        <H2 id="if-you-dont-qualify">If you don&rsquo;t meet your insurer&rsquo;s criteria</H2>
        <UL>
          <li>
            <strong>Self-pay.</strong> Out-of-pocket, insurer BMI thresholds don&rsquo;t apply, and
            manufacturer direct-pay pricing has fallen substantially in the past year. See{" "}
            <Link
              to="/medical-weight-loss/insurance/cash-pay-denials"
              preload="intent"
              className="text-brand-deep underline hover:no-underline"
            >
              cash-pay options and denials
            </Link>
            .
          </li>
          <li>
            <strong>A different indication.</strong> These drugs are also approved for sleep apnea,
            cardiovascular risk reduction, and MASH liver disease &mdash; often an easier coverage
            pathway if you have one of those conditions.
          </li>
          <li>
            <strong>A stronger submission or an appeal.</strong> Many denials come down to
            incomplete documentation rather than genuine ineligibility. Our{" "}
            <Link
              to="/medical-weight-loss/insurance"
              preload="intent"
              className="text-brand-deep underline hover:no-underline"
            >
              Virginia insurance coverage guide
            </Link>{" "}
            explains the process.
          </li>
        </UL>

        <Callout>
          <strong>Not sure where you stand?</strong> Bring your insurance card to your consultation.
          We&rsquo;ll check your plan&rsquo;s criteria, review whether you meet them, and talk
          through your options if you don&rsquo;t &mdash; including what self-pay would actually
          cost.
        </Callout>

        <H2 id="faqs">Frequently asked questions</H2>
        <FaqList faqs={FAQS} />

        <H2 id="sources">Sources</H2>
        <P>
          FDA-approved prescribing information for each medication:
        </P>
        <UL>
          {LABEL_SOURCES.map((s) => (
            <li key={s.href}>
              <a
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-deep underline hover:no-underline"
              >
                {s.label}
              </a>
            </li>
          ))}
        </UL>
        <P>Payor coverage policies and prior authorization criteria:</P>
        <UL>
          {PLAN_SOURCES.map((s) => (
            <li key={s.href}>
              <a
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-deep underline hover:no-underline"
              >
                {s.label}
              </a>
            </li>
          ))}
        </UL>


        <aside className="mt-8 pt-4 border-t border-border text-sm text-muted-foreground space-y-2 leading-snug">
          <p id="fn1">
            <sup>1</sup> Under federal law, the FDA-approved prescribing information &mdash;
            commonly just called &ldquo;the label&rdquo; &mdash; sets out the uses the agency has
            found supported by adequate evidence. Manufacturers may not promote a drug for uses
            outside that approved labeling, so approved indications are effectively the only ones
            that appear in advertising and promotional material. That is the origin of the phrase
            &ldquo;on-label use.&rdquo; The restriction governs what manufacturers may claim, not
            what physicians may prescribe: off-label prescribing by a licensed clinician is lawful
            and is a routine part of medical practice.{" "}
            <a href="#fnref1" className="text-brand-deep no-underline">
              &#8617;
            </a>
          </p>
          <p>
            <em>
              This page is general information, not medical advice. Insurance criteria change
              frequently; requirements described here were current as of July 2026 and should be
              confirmed with your plan.
            </em>
          </p>
        </aside>
      </PageSection>

      <InsuranceCTA />
    </>
  );
}
